import { useCallback } from "react";
import SectionCard from "components/cms/SectionCard";
import CategoryPicker from "components/cms/CategoryPicker";
import {
  AreaInput,
  CardRow,
  FieldRow,
  FileInput,
  GroupBox,
  TextInput,
} from "components/cms/fields";
import { emptyFact, emptyStat, listCustomerCases } from "api/customercase";
import { uniqueOptions } from "utils/cms";

export default function CaseStack({ form, setForm, setField, frMap }) {
  const facts = form.facts || [];
  const numbers = form.numbers || [];
  const loadIndustries = useCallback(async () => {
    const res = await listCustomerCases();
    return uniqueOptions(res.data?.data?.items, "industrySlug", "industryLabel");
  }, []);

  const addFact = () => setForm((prev) => ({ ...prev, facts: [...(prev.facts || []), emptyFact()] }));
  const addStat = () => setForm((prev) => ({ ...prev, numbers: [...(prev.numbers || []), emptyStat()] }));
  const removeAt = (key, index) =>
    setForm((prev) => ({ ...prev, [key]: (prev[key] || []).filter((_, i) => i !== index) }));

  return (
    <>
      <SectionCard title="Customer case" description="Same template as every case study page">
        <FieldRow>
          <TextInput label="Slug" path="slug" form={form} setField={setField} placeholder="awnings" />
          <TextInput label="Client" path="client" form={form} setField={setField} />
        </FieldRow>
        <FieldRow>
          <TextInput label="Year" path="year" form={form} setField={setField} type="number" />
          <TextInput label="Service" path="service" form={form} setField={setField} frMap={frMap} />
        </FieldRow>
        <AreaInput label="Title" path="title" form={form} setField={setField} frMap={frMap} rows={2} />
        <AreaInput label="Summary" path="summary" form={form} setField={setField} frMap={frMap} rows={3} />
        <CategoryPicker
          label="Industry"
          slugPath="industrySlug"
          labelPath="industryLabel"
          form={form}
          setField={setField}
          frMap={frMap}
          loadOptions={loadIndustries}
          newLabel="Add new industry"
        />
      </SectionCard>

      <SectionCard title="Images">
        <CardRow>
          <FileInput label="Logo" path="logo" form={form} setField={setField} extra="" />
          <FileInput label="Hero image" path="heroImage" form={form} setField={setField} extra="" />
        </CardRow>
      </SectionCard>

      <SectionCard title="Facts">
        <div className="flex flex-col gap-3">
          {facts.map((_, index) => (
            <GroupBox key={`fact-${index}`} title={`Fact ${index + 1}`}>
              <FieldRow>
                <TextInput label="Label" path={`facts.${index}.label`} form={form} setField={setField} frMap={frMap} />
                <TextInput label="Value" path={`facts.${index}.value`} form={form} setField={setField} frMap={frMap} />
              </FieldRow>
              <button type="button" onClick={() => removeAt("facts", index)} className="text-xs text-red-600 hover:underline">
                Remove
              </button>
            </GroupBox>
          ))}
        </div>
        <button type="button" onClick={addFact} className="mt-3 rounded-lg border border-purple-200 px-3 py-1.5 text-xs font-medium text-[#6A4CF0]">
          Add fact
        </button>
      </SectionCard>

      <SectionCard title="Numbers after launch">
        <div className="flex flex-col gap-3">
          {numbers.map((item, index) => (
            <GroupBox key={`stat-${index}`} title={`Stat ${index + 1}`}>
              <FieldRow>
                <TextInput label="Value" path={`numbers.${index}.value`} form={form} setField={setField} />
                <TextInput label="Label" path={`numbers.${index}.label`} form={form} setField={setField} frMap={frMap} />
              </FieldRow>
              <label className="flex items-center gap-2 text-sm text-navy-700 dark:text-white">
                <input
                  type="checkbox"
                  checked={!!item.accent}
                  onChange={(event) => setField(`numbers.${index}.accent`, event.target.checked)}
                />
                Accent colour
              </label>
              <button type="button" onClick={() => removeAt("numbers", index)} className="text-xs text-red-600 hover:underline">
                Remove
              </button>
            </GroupBox>
          ))}
        </div>
        <button type="button" onClick={addStat} className="mt-3 rounded-lg border border-purple-200 px-3 py-1.5 text-xs font-medium text-[#6A4CF0]">
          Add number
        </button>
      </SectionCard>

      <SectionCard title="Brief, solution and stack">
        <AreaInput label="Brief" path="brief" form={form} setField={setField} frMap={frMap} rows={5} />
        <AreaInput label="Solution" path="solution" form={form} setField={setField} frMap={frMap} rows={5} />
        <TextInput label="Technology used" path="techUsed" form={form} setField={setField} frMap={frMap} />
        <TextInput label="Tech stack (comma separated)" path="stack" form={form} setField={setField} />
      </SectionCard>

      <SectionCard title="Client quote">
        <AreaInput label="Quote" path="quoteText" form={form} setField={setField} frMap={frMap} rows={3} />
        <FieldRow>
          <TextInput label="Author" path="quoteAuthor" form={form} setField={setField} />
          <TextInput label="Role" path="quoteRole" form={form} setField={setField} frMap={frMap} />
        </FieldRow>
      </SectionCard>
    </>
  );
}
